import type { ActionButton } from '@app/src/components/ActionButtons'
import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useProvidersModal } from '@app/src/context/ProvidersModalContext'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import { useScheduleEnrichment } from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleEnrichment/hooks/scheduleEnrichment'
import { useAlreadyExistingReportedData } from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleScreening/hooks/alreadyExistingReportedData'
import { getScreeningInfo } from '@app/src/utils/getScreeningInfo'
import { validURL } from '@app/src/utils/helpers'
import { insertIf } from '@app/src/utils/helpersTs'
import { canProviderBeEnriched, resolveProviderWebsiteAddress } from '@app/src/utils/providerEnrichmentUtils'
import type { ButtonProps } from '@mui/material'
import { Dialog, Stack } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useConfirm } from 'material-ui-confirm'
import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import EnrichmentScheduledStep from './EnrichmentScheduledStep'
import { useScheduleScreening } from './hooks/scheduleScreening'
import ProvidersMissingWebsite from './ProvidersMissingWebsite'
import ReportedDataFoundStep from './ReportedDataFoundStep'
import ScheduleProviderScreeningSummary from './ScheduleProviderScreeningSummary'
import ScheduleScreeningSuccessView from './ScheduleScreeningSuccessView'
import SelectTemplateForScraping from './SelectTemplateForScraping'
import WebsiteMissingChoiceStep, { WebsiteMissingOption } from './WebsiteMissingChoiceStep'
import WebsiteNotFoundStep from './WebsiteNotFoundStep'

export enum SCHEDULE_SCREENING_QUESTION_KEYS {
  PROVIDER_IDS = 'providerIds',
  TEMPLATES = 'templates',
  SEND_EMAIL_ON_COMPLETE = 'sendEmailOnComplete',
}

type ScheduleProviderScreeningModalProps = {
  open: boolean
  onClose: () => void
}

export type ScheduleScreeningFormData = {
  providerIds: number[]
  templates: Array<{ id: number; title: string }>
  sendEmailOnComplete: boolean
}

const WIDTH = 584

const ScheduleProviderScreeningModal: React.FC<ScheduleProviderScreeningModalProps> = ({ open, onClose }) => {
  const { formatMessage } = useIntl()
  const { activeStep, totalSteps, setActiveStep, setTotalSteps } = useCreationModalProgress()
  const { providersToConnect, templates, hideViewScreenedRequestsButton, stepOffset } = useProvidersModal()
  const { scheduleScreening, isScheduling } = useScheduleScreening()
  const { scheduleEnrichment, isScheduling: isSchedulingEnrichment } = useScheduleEnrichment()
  const confirm = useConfirm()
  const { canScheduleMultipleEnrichments = false } = useFlags()
  const [showSuccessScreen, setShowSuccessScreen] = useState(false)
  const [showReportedDataFoundStep, setShowReportedDataFoundStep] = useState(false)
  const [websiteMissingChoice, setWebsiteMissingChoice] = useState<WebsiteMissingOption | null>(null)
  const [enrichmentScheduled, setEnrichmentScheduled] = useState(false)
  const [creditsUsed, setCreditsUsed] = useState(0)
  const { settings } = useOrganizationSettings()
  const formMethods = useForm<ScheduleScreeningFormData>({
    shouldUnregister: false,
    defaultValues: {
      [SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS]: [],
      [SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES]: [],
      [SCHEDULE_SCREENING_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE]: false,
    },
  })

  const { handleSubmit, reset, watch, setValue } = formMethods

  const hasPreselectedTemplates = Boolean(templates?.length)
  const remainingProviderScreenings = settings?.remainingProviderScreenings ?? 0

  const watchProviders = watch(SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS)
  const watchTemplates = watch(SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES)
  const watchSendEmail = watch(SCHEDULE_SCREENING_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE)
  const templateIds = watchTemplates?.map(q => q.id) ?? []
  const { alreadyExistingReportedData, alreadyExistingReportedDataLoading, alreadyExistingReportedDataFetched } =
    useAlreadyExistingReportedData({ enabled: activeStep > 0, templateIds })

  const { alreadyScreenedCount, validSelectedProviderIds } = useMemo(
    () => getScreeningInfo(alreadyExistingReportedData, providersToConnect, watchTemplates, watchProviders),
    [alreadyExistingReportedData, providersToConnect, watchTemplates, watchProviders],
  )

  const totalScreeningsToPerform = templateIds.length * watchProviders.length - alreadyScreenedCount
  const remainingScreeningsEnough = remainingProviderScreenings - totalScreeningsToPerform >= 0

  const providersMissingWebsite = providersToConnect?.filter(
    provider => validURL(resolveProviderWebsiteAddress(provider)) === false,
  )

  const enrichmentEligibleProviders = providersMissingWebsite.filter(provider =>
    canProviderBeEnriched(provider, canScheduleMultipleEnrichments),
  )

  const nonEligibleMissingWebsite = providersMissingWebsite.filter(
    provider => !canProviderBeEnriched(provider, canScheduleMultipleEnrichments),
  )

  const hasEnrichmentEligible = enrichmentEligibleProviders.length > 0

  const allProvidersBeingEnriched =
    websiteMissingChoice === WebsiteMissingOption.AutoFill &&
    validSelectedProviderIds.length === 0 &&
    nonEligibleMissingWebsite.length === 0

  const handleWebsiteChoiceNext = async () => {
    if (
      websiteMissingChoice === WebsiteMissingOption.AutoFill &&
      enrichmentEligibleProviders.length > 0 &&
      !enrichmentScheduled
    ) {
      await scheduleEnrichment({ providerIds: enrichmentEligibleProviders.map(p => p.id) })
      setEnrichmentScheduled(true)
    }
  }

  const allDataWeWantToScrapeAlreadyExists =
    providersToConnect?.length * watchTemplates?.length === alreadyExistingReportedData?.length

  const onSubmit = async (values: ScheduleScreeningFormData) => {
    if (values.providerIds.length === 0) {
      onClose()
      return
    }

    await scheduleScreening({
      providerIds: values.providerIds,
      templateIds: values.templates.map(template => template.id),
      sendEmailOnComplete: values.sendEmailOnComplete,
    })

    setCreditsUsed(totalScreeningsToPerform)
    setShowSuccessScreen(true)
  }

  const QUESTIONS = [
    ...insertIf(!hasPreselectedTemplates, {
      title: formatMessage({ id: 'form.scheduleProviderScreening.selectQuestionnaire.title' }),
      fieldnames: [SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES],
      disableInitialBack: true,
      overrideButtonTextId: 'general.continue',
      widthOverride: WIDTH,
      children: <SelectTemplateForScraping name={SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES} />,
    }),
    ...insertIf(showReportedDataFoundStep, {
      title: formatMessage({ id: 'form.scheduleProviderScreening.reportedDataFound.title' }),
      extraButtons: [
        {
          label: formatMessage({ id: 'general.learnMore' }),
          variant: 'text',
          onClick: () =>
            confirm({
              title: formatMessage({ id: 'form.scheduleProviderScreening.reportedDataFound.dialogTitle' }),
              description: formatMessage({
                id: 'form.scheduleProviderScreening.reportedDataFound.dialogDescription',
              }),
              confirmationText: formatMessage({ id: 'general.close' }),
              hideCancelButton: true,
            }),
        },
      ] as ActionButton[],
      nextButtonTooltip: allDataWeWantToScrapeAlreadyExists
        ? formatMessage({
            id: 'form.scheduleProviderScreening.reportedDataFound.descriptionAllScreenedAlreadyReported',
          })
        : undefined,
      description: formatMessage({
        id: allDataWeWantToScrapeAlreadyExists
          ? 'form.scheduleProviderScreening.reportedDataFound.descriptionAllScreenedAlreadyReported'
          : 'form.scheduleProviderScreening.reportedDataFound.description',
      }),
      fieldnames: [],
      widthOverride: WIDTH,
      canGoNext: () => !allDataWeWantToScrapeAlreadyExists,
      children: (
        <ReportedDataFoundStep
          alreadyExistingReportedData={alreadyExistingReportedData}
          alreadyExistingReportedDataLoading={alreadyExistingReportedDataLoading}
        />
      ),
    }),
    ...insertIf(providersMissingWebsite.length > 0 && hasEnrichmentEligible, {
      title: formatMessage({ id: 'form.scheduleProviderScreening.websiteChoice.title' }),
      fieldnames: [],
      widthOverride: WIDTH,
      disableInitialBack: hasPreselectedTemplates,
      canGoNext: () => websiteMissingChoice !== null,
      customOnClickNext: handleWebsiteChoiceNext,
      isLoading: isSchedulingEnrichment,
      children: (
        <WebsiteMissingChoiceStep
          missingCount={providersMissingWebsite.length}
          selectedOption={websiteMissingChoice}
          onOptionChange={setWebsiteMissingChoice}
        />
      ),
    }),
    ...insertIf(hasEnrichmentEligible && websiteMissingChoice === WebsiteMissingOption.AutoFill, {
      title: formatMessage({ id: 'form.scheduleProviderScreening.enrichmentScheduled.title' }),
      fieldnames: [],
      overrideButtonTextId: allProvidersBeingEnriched ? 'general.close' : 'general.continue',
      children: <EnrichmentScheduledStep enrichedCount={enrichmentEligibleProviders.length} />,
    }),
    ...insertIf(
      hasEnrichmentEligible &&
        websiteMissingChoice === WebsiteMissingOption.AutoFill &&
        nonEligibleMissingWebsite.length > 0,
      {
        title: formatMessage({ id: 'form.scheduleProviderScreening.websiteNotFound.title' }),
        fieldnames: [SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS],
        overrideButtonTextId: 'general.skip',
        nextButtonProps: { variant: 'text', endIcon: null } as ButtonProps,
        widthOverride: WIDTH,
        children: <WebsiteNotFoundStep providersMissingWebsite={nonEligibleMissingWebsite} />,
      },
    ),
    ...insertIf(providersMissingWebsite.length > 0 && !hasEnrichmentEligible, {
      title: formatMessage({ id: 'form.scheduleProviderScreening.websiteNotFound.title' }),
      fieldnames: [SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS],
      overrideButtonTextId: 'general.skip',
      nextButtonProps: { variant: 'text', endIcon: null } as ButtonProps,
      widthOverride: WIDTH,
      disableInitialBack: hasPreselectedTemplates,
      children: <WebsiteNotFoundStep providersMissingWebsite={providersMissingWebsite} />,
    }),
    ...insertIf(hasEnrichmentEligible && websiteMissingChoice === WebsiteMissingOption.Manual, {
      title: formatMessage({ id: 'form.scheduleProviderScreening.websiteMissing.addWebsite' }),
      description: formatMessage(
        { id: 'form.scheduleProviderScreening.websiteMissing.description' },
        {
          count: watchProviders.length,
        },
      ),
      fieldnames: [SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS],
      overrideButtonTextId: 'general.skip',
      nextButtonProps: { variant: 'text', endIcon: null } as ButtonProps,
      widthOverride: WIDTH,
      children: <ProvidersMissingWebsite providersMissingWebsite={providersMissingWebsite} />,
    }),
    ...insertIf(!allProvidersBeingEnriched, {
      title: formatMessage({ id: 'form.scheduleProviderScreening.summary.title' }),
      description: formatMessage({ id: 'form.scheduleProviderScreening.summary.description' }),
      fieldnames: [],
      hideNextButton: true,
      disableInitialBack: true,
      widthOverride: WIDTH,
      inlineButtons: true,
      children: (
        <ScheduleProviderScreeningSummary
          remainingProviderScreenings={remainingProviderScreenings}
          totalScreeningsToPerform={totalScreeningsToPerform}
          remainingScreeningsEnough={remainingScreeningsEnough}
          sendEmailOnComplete={watchSendEmail}
          onEmailToggle={enabled => setValue(SCHEDULE_SCREENING_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE, enabled)}
          isLoading={isScheduling}
          onSubmit={handleSubmit(onSubmit)}
        />
      ),
    }),
  ]

  const activeQuestion = useMemo(() => QUESTIONS[activeStep - 1], [activeStep, QUESTIONS])

  useEffect(() => {
    if (activeStep === 1 && alreadyExistingReportedDataFetched) {
      setShowReportedDataFoundStep(alreadyExistingReportedData.length > 0)
    }
  }, [alreadyExistingReportedData, activeStep])

  // Only set initial values when modal opens
  useEffect(() => {
    if (open && providersToConnect?.length) {
      reset({
        [SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS]: validSelectedProviderIds,
        [SCHEDULE_SCREENING_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE]: false,
      })
    }

    return () => {
      setActiveStep(1)
      reset()
      setShowSuccessScreen(false)
      setWebsiteMissingChoice(null)
      setEnrichmentScheduled(false)
      setCreditsUsed(0)
    }
  }, [open])

  // Auto-select email checkbox when screening count >= 20
  useEffect(() => {
    if (open && totalScreeningsToPerform >= 20) {
      setValue(SCHEDULE_SCREENING_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE, true)
    }
  }, [open, totalScreeningsToPerform, setValue])

  // Set templates when provided
  useEffect(() => {
    if (open && templates?.length) {
      setValue(SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES, templates)
    }
  }, [open, templates, setValue])

  useEffect(() => {
    setTotalSteps(QUESTIONS.length)
    if (QUESTIONS.length < activeStep) {
      setActiveStep(QUESTIONS.length)
    }
  }, [QUESTIONS.length])

  return (
    <Dialog open={open} onClose={onClose} fullScreen closeAfterTransition>
      {showSuccessScreen ? (
        <ScheduleScreeningSuccessView
          creditsUsed={creditsUsed}
          onClose={onClose}
          hideViewScreenedRequests={hideViewScreenedRequestsButton}
        />
      ) : (
        <CreationModalContainer
          title={formatMessage({ id: 'general.aiScreening' })}
          onClose={onClose}
          displayedActiveStep={activeStep + stepOffset}
          displayedTotalSteps={totalSteps + stepOffset}
        >
          <FormProvider {...formMethods}>
            <Stack flexGrow={1} alignItems="center">
              {activeQuestion && (
                <CreationModalStep {...activeQuestion} widthOverride={WIDTH}>
                  {activeQuestion.children}
                </CreationModalStep>
              )}
            </Stack>
          </FormProvider>
        </CreationModalContainer>
      )}
    </Dialog>
  )
}

export default ScheduleProviderScreeningModal
